import { Button, EmptyState, Table, type TableSortDirection } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { UnallocatedColumnsState } from '../hooks/useUnallocatedColumns';
import type { UnallocatedFilterState } from '../hooks/useUnallocatedFilters';
import type { FilterOption, UnallocatedContract, UnallocatedSortField } from '../types/Checks';
import { COLUMN_MIN_WIDTH, WIDE_TABLE_CONTAINER } from './wideTable';

type UnallocatedTableProps = {
  items: UnallocatedContract[];
  filterOptions: { contractTypes: FilterOption[]; contractStatuses: FilterOption[] };
  filterState: UnallocatedFilterState;
  columnsState: UnallocatedColumnsState;
};

const toTableOptions = (options: FilterOption[]) =>
  options.map((option) => ({ value: String(option.id), label: option.name }));

/** Uma linha por contrato. Ocultar uma coluna não remove o filtro dela: o chip continua. */
export function UnallocatedTable({
  items,
  filterOptions,
  filterState,
  columnsState,
}: UnallocatedTableProps) {
  const { t } = useTranslation('alocacao');
  const { filters, setSort, setSearch, setIds } = filterState;
  const { isVisible, visibleColumns } = columnsState;

  const sortProps = (field: UnallocatedSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  return (
    <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
      <Table.Caption className="sr-only">{t('consultas.semAlocacao.titulo')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          {isVisible('registrationNumber') && (
            <Table.Header align="right" {...sortProps('registrationNumber')}>
              {t('consultas.colunas.matricula')}
            </Table.Header>
          )}
          {isVisible('professional') && (
            <Table.Header
              className={COLUMN_MIN_WIDTH.medium}
              {...sortProps('professional')}
              filter={
                <Table.TextFilter
                  label={t('consultas.colunas.profissional')}
                  placeholder={t('consultas.filtros.nomeOuMatriculaPlaceholder')}
                  value={filters.professionalSearch ?? ''}
                  onValueChange={(value) => setSearch('professionalSearch', value)}
                />
              }
            >
              {t('consultas.colunas.profissional')}
            </Table.Header>
          )}
          {isVisible('contractType') && (
            <Table.Header
              {...sortProps('contractType')}
              filter={
                <Table.OptionsFilter
                  label={t('consultas.colunas.tipo')}
                  options={toTableOptions(filterOptions.contractTypes)}
                  value={(filters.contractTypeIds ?? []).map(String)}
                  onValueChange={(values) => setIds('contractTypeIds', values.map(Number))}
                />
              }
            >
              {t('consultas.colunas.tipo')}
            </Table.Header>
          )}
          {isVisible('profile') && (
            <Table.Header
              className={COLUMN_MIN_WIDTH.short}
              {...sortProps('profile')}
              filter={
                <Table.TextFilter
                  label={t('consultas.colunas.perfil')}
                  value={filters.profileSearch ?? ''}
                  onValueChange={(value) => setSearch('profileSearch', value)}
                />
              }
            >
              {t('consultas.colunas.perfil')}
            </Table.Header>
          )}
          {isVisible('contractStatus') && (
            <Table.Header
              {...sortProps('contractStatus')}
              filter={
                <Table.OptionsFilter
                  label={t('consultas.colunas.situacao')}
                  options={toTableOptions(filterOptions.contractStatuses)}
                  value={(filters.contractStatusIds ?? []).map(String)}
                  onValueChange={(values) => setIds('contractStatusIds', values.map(Number))}
                />
              }
            >
              {t('consultas.colunas.situacao')}
            </Table.Header>
          )}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.length === 0 ? (
          // Vazio por filtro mantém o cabeçalho: é nele que o filtro se ajusta.
          <Table.Row>
            <Table.Cell colSpan={visibleColumns.length}>
              <EmptyState
                title={t('consultas.semAlocacao.filtroSemResultadoTitulo')}
                description={t('consultas.semAlocacao.filtroSemResultadoDescricao')}
                action={
                  <Button variant="secondary" onClick={filterState.clearFilters}>
                    {t('filtros.limpar')}
                  </Button>
                }
              />
            </Table.Cell>
          </Table.Row>
        ) : (
          items.map((contract) => (
            <Table.Row key={contract.professionalContractId}>
              {isVisible('registrationNumber') && (
                <Table.Cell align="right" className="tabular-nums">
                  {contract.registrationNumber ?? '—'}
                </Table.Cell>
              )}
              {isVisible('professional') && <Table.Cell>{contract.professionalName}</Table.Cell>}
              {isVisible('contractType') && <Table.Cell>{contract.contractType ?? '—'}</Table.Cell>}
              {isVisible('profile') && <Table.Cell>{contract.profile || '—'}</Table.Cell>}
              {isVisible('contractStatus') && (
                <Table.Cell>{contract.contractStatus ?? '—'}</Table.Cell>
              )}
            </Table.Row>
          ))
        )}
      </Table.Body>
    </Table>
  );
}
